import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  Select,
  Progress,
  Kbd,
  useColorModeValue,
} from '@chakra-ui/react';
import { useToast } from '../hooks/useToast';
import fetchWithAuth from '../utils/fetchWithAuth';

function SendQueue({ pollId, poll, onSendComplete }) {
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [trancheFilter, setTrancheFilter] = useState('');
  const [serverConfig, setServerConfig] = useState(null);

  const sendButtonRef = useRef(null);
  const toast = useToast();

  const boxBgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/polls/${pollId}/send-queue/`;
      if (trancheFilter) {
        url += `?tranche=${trancheFilter}`;
      }
      const response = await fetchWithAuth(url);
      if (response.ok) {
        const data = await response.json();
        setQueue(data.queue || []);
        setSummary(data.summary || null);
      }
    } catch (err) {
      console.error('Failed to fetch send queue:', err);
    } finally {
      setLoading(false);
    }
  }, [pollId, trancheFilter]);

  const fetchServerConfig = async () => {
    try {
      const response = await fetchWithAuth('/api/server-config/');
      if (response.ok) {
        setServerConfig(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch server config:', err);
    }
  };

  const checkBatchStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/batch-send/status/`);
      if (response.ok) {
        const data = await response.json();
        setBatchRunning(data.running);
        return data.running;
      }
    } catch (err) {
      console.error('Failed to check batch status:', err);
    }
    return false;
  }, [pollId]);

  useEffect(() => {
    fetchQueue();
    fetchServerConfig();
    checkBatchStatus();
  }, [fetchQueue, checkBatchStatus]);

  // Auto-refresh while batch is running (every 5 seconds)
  useEffect(() => {
    if (!batchRunning) return;
    const interval = setInterval(async () => {
      await fetchQueue();
      const stillRunning = await checkBatchStatus();
      if (!stillRunning) {
        toast.showSuccess('Batch Complete', 'Server-side batch send has finished.');
        if (onSendComplete) onSendComplete();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [batchRunning, fetchQueue, checkBatchStatus, onSendComplete, toast]);

  // Keyboard shortcut: Enter or Space to send next
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only when not in an input/select/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!sending && nextPending) {
          handleSendNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const nextPending = queue.find(r => r.sms_status === 'pending' && !r.is_opted_out);
  const pendingItems = queue.filter(r => r.sms_status === 'pending');

  const handleSendOne = async (respondentId) => {
    setSending(true);
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/send-sms/${respondentId}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQueue(prev => prev.map(item =>
          item.id === respondentId
            ? { ...item, sms_status: 'sent', sms_sent_at: data.sms_sent_at, sms_message_sid: data.sms_message_sid }
            : item
        ));
        setSummary(prev => prev ? {
          ...prev,
          pending: prev.pending - 1,
          sent: prev.sent + 1,
        } : prev);

        if (onSendComplete) onSendComplete();
      } else {
        setQueue(prev => prev.map(item =>
          item.id === respondentId
            ? { ...item, sms_status: 'failed' }
            : item
        ));
        setSummary(prev => prev ? {
          ...prev,
          pending: prev.pending - 1,
          failed: prev.failed + 1,
        } : prev);
        toast.showError('Send Failed', data.error || 'Failed to send SMS');
      }
    } catch (err) {
      toast.showError('Error', err.message);
    } finally {
      setSending(false);
      if (sendButtonRef.current) {
        sendButtonRef.current.focus();
      }
    }
  };

  const handleSendNext = () => {
    if (nextPending) {
      handleSendOne(nextPending.id);
    }
  };

  const handleStartBatch = async () => {
    try {
      const body = { delay: 1.0 };
      if (trancheFilter) body.tranche = parseInt(trancheFilter);

      const response = await fetchWithAuth(`/api/polls/${pollId}/batch-send/start/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.started) {
        setBatchRunning(true);
        toast.showSuccess('Batch Started', `Sending ${data.pending_count} messages on server (${data.delay}s delay). You can close this page.`);
      } else {
        toast.showError('Error', data.error || 'Failed to start batch');
      }
    } catch (err) {
      toast.showError('Error', err.message);
    }
  };

  const handleStopBatch = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/batch-send/stop/`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.showSuccess('Stopping', 'Batch send will stop after the current message.');
        // Will pick up the stopped state on next status check
      }
    } catch (err) {
      toast.showError('Error', err.message);
    }
  };

  // Get unique tranche numbers from queue
  const tranches = [...new Set(queue.map(r => r.tranche))].sort((a, b) => a - b);

  const isDryRun = serverConfig?.twilio_dry_run;
  const progressPct = summary ? ((summary.sent / Math.max(summary.total - summary.not_applicable, 1)) * 100) : 0;

  const statusBadge = (smsStatus) => {
    const map = {
      pending: { color: 'yellow', label: 'Pending' },
      sent: { color: 'green', label: 'Sent' },
      failed: { color: 'red', label: 'Failed' },
      not_applicable: { color: 'gray', label: 'N/A' },
    };
    const config = map[smsStatus] || map.pending;
    return <Badge colorScheme={config.color} fontSize="xs">{config.label}</Badge>;
  };

  return (
    <Box>
      {/* Mode indicator */}
      {serverConfig && isDryRun && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <strong>DRY RUN MODE</strong> -- SMS will be simulated, not actually sent.
          </Text>
        </Alert>
      )}
      {serverConfig && !isDryRun && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <strong>LIVE MODE</strong> -- Each click sends a real SMS to a real phone number.
          </Text>
        </Alert>
      )}

      {/* Summary + Send Next */}
      {summary && (
        <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor} mb={4}>
          <HStack justify="space-between" mb={3}>
            <VStack align="start" spacing={1}>
              <Heading size="sm">Send Queue</Heading>
              <HStack spacing={4}>
                <Text fontSize="sm" color="gray.600">
                  {summary.sent} sent
                </Text>
                <Text fontSize="sm" color="yellow.600" fontWeight="600">
                  {summary.pending} pending
                </Text>
                {summary.failed > 0 && (
                  <Text fontSize="sm" color="red.500">
                    {summary.failed} failed
                  </Text>
                )}
                {summary.costs && (
                  <Text fontSize="sm" color="gray.500">
                    ${summary.costs.total_spent} spent
                  </Text>
                )}
              </HStack>
            </VStack>
            <VStack align="end" spacing={1}>
              <HStack spacing={2}>
                <Button
                  ref={sendButtonRef}
                  colorScheme="blue"
                  size="md"
                  onClick={handleSendNext}
                  isLoading={sending}
                  isDisabled={!nextPending || sending || batchRunning}
                  loadingText="Sending..."
                >
                  Send Next
                </Button>
                {!batchRunning ? (
                  <Button
                    colorScheme="green"
                    size="md"
                    onClick={handleStartBatch}
                    isDisabled={!nextPending || sending}
                  >
                    Send All ({pendingItems.filter(r => !r.is_opted_out).length})
                  </Button>
                ) : (
                  <Button
                    colorScheme="red"
                    size="md"
                    onClick={handleStopBatch}
                  >
                    Stop
                  </Button>
                )}
              </HStack>
              {!batchRunning && (
                <Text fontSize="xs" color="gray.500">
                  Press <Kbd>Enter</Kbd> or <Kbd>Space</Kbd>
                </Text>
              )}
              {batchRunning && (
                <Text fontSize="xs" color="green.500" fontWeight="600">
                  Sending on server (2s delay) -- auto-refreshing
                </Text>
              )}
            </VStack>
          </HStack>

          {/* Progress bar */}
          <Progress
            value={progressPct}
            colorScheme="blue"
            size="sm"
            borderRadius="md"
            mb={2}
            hasStripe={batchRunning}
            isAnimated={batchRunning}
          />
          <Text fontSize="xs" color="gray.500" textAlign="right">
            {summary.sent} / {summary.total - summary.not_applicable} sendable
          </Text>
        </Box>
      )}

      {/* Next up preview */}
      {nextPending && !batchRunning && (
        <Box bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor={borderColor} mb={4}>
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="0.05em">
            Next Up
          </Text>
          <HStack justify="space-between" mb={2}>
            <HStack spacing={3}>
              <Text fontWeight="600">{nextPending.first_name || nextPending.external_id}</Text>
              <Text fontSize="sm" fontFamily="mono" color="gray.600">{nextPending.phone_number}</Text>
              {nextPending.invitation_variant && (
                <Badge colorScheme="purple" fontSize="xs">Variant {nextPending.invitation_variant}</Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.500">Tranche {nextPending.tranche}</Text>
          </HStack>
          <Box
            bg="gray.50"
            p={3}
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            fontSize="sm"
            whiteSpace="pre-wrap"
            color="gray.700"
          >
            {nextPending.message_preview}
          </Box>
        </Box>
      )}

      {/* Tranche filter */}
      <HStack mb={4} spacing={3}>
        <Text fontSize="sm" fontWeight="500">Filter by tranche:</Text>
        <Select
          size="sm"
          maxW="200px"
          value={trancheFilter}
          onChange={e => setTrancheFilter(e.target.value)}
        >
          <option value="">All tranches</option>
          {tranches.map(t => (
            <option key={t} value={t}>Tranche {t}</option>
          ))}
        </Select>
        <Button size="sm" variant="outline" onClick={fetchQueue} isLoading={loading}>
          Refresh
        </Button>
      </HStack>

      {/* Queue table */}
      {queue.length === 0 ? (
        <Text color="gray.500" py={4}>No respondents in queue. Generate a tranche first.</Text>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Phone</Th>
                <Th>Tranche</Th>
                {poll?.invitation_variants && <Th>Variant</Th>}
                <Th>SMS Status</Th>
                <Th>Survey</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {queue.map(item => (
                <Tr
                  key={item.id}
                  bg={item.id === nextPending?.id ? 'blue.50' : undefined}
                  opacity={item.sms_status === 'sent' ? 0.6 : 1}
                >
                  <Td fontWeight={item.id === nextPending?.id ? '600' : '400'}>
                    {item.first_name || item.external_id}
                  </Td>
                  <Td fontFamily="mono" fontSize="xs">{item.phone_number || '--'}</Td>
                  <Td>{item.tranche}</Td>
                  {poll?.invitation_variants && (
                    <Td>{item.invitation_variant || '--'}</Td>
                  )}
                  <Td>
                    {item.is_opted_out ? (
                      <Badge colorScheme="red" fontSize="xs">Opted Out</Badge>
                    ) : statusBadge(item.sms_status)}
                  </Td>
                  <Td>
                    {item.has_completed ? (
                      <Badge colorScheme="green" fontSize="xs">Completed</Badge>
                    ) : item.sms_status === 'sent' ? (
                      <Badge colorScheme="blue" fontSize="xs">Awaiting</Badge>
                    ) : null}
                  </Td>
                  <Td>
                    {item.sms_status === 'pending' && !item.is_opted_out && !batchRunning && (
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => handleSendOne(item.id)}
                        isDisabled={sending}
                      >
                        Send
                      </Button>
                    )}
                    {item.sms_status === 'failed' && !batchRunning && (
                      <Button
                        size="xs"
                        colorScheme="orange"
                        variant="outline"
                        onClick={() => {
                          setQueue(prev => prev.map(q =>
                            q.id === item.id ? { ...q, sms_status: 'pending' } : q
                          ));
                          handleSendOne(item.id);
                        }}
                        isDisabled={sending}
                      >
                        Retry
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default SendQueue;
